/**
 * GTM Banderole
 * see TUB-22323 for usage example
 */
import OcmMessenger from "ocm_mercurius_messenger";
import { messengerPublicationTypes } from "@ocm/services/services.constants";
import { pushGA4CustomEvent } from "@ocm/services/analytics4Tracking";
import { showModal } from "./infoModals";
// type import does not work here
// import type { GlobalRouterMessengerData } from "@ocm/types/OcmMessenger.type";

const banderoleEl = document.getElementById("js-banderole");
const cartBanderoleEl = document.getElementById("js-cart-banderole");
const isAuthenticated = window.recommendationData.Recommendation.customerData.authlevel > 0;

type GlobalRouterMessengerData = {
	data: { mountedApps: string[]; to: string };
};
OcmMessenger.subscribe(
	messengerPublicationTypes.ROUTE_CHANGE_MOUNTED_APPS,
	({ data: { mountedApps } }: GlobalRouterMessengerData) => {
		if (mountedApps.includes("mf-cart")) {
			_hide(banderoleEl);
			_slideIn(cartBanderoleEl);
			return;
		}
		_hide(cartBanderoleEl);
		_slideIn(banderoleEl);
	}
);

function _isCartPage() {
	return window.location.pathname.includes("/cart");
}

function _hide(el: HTMLElement | null) {
	if (!el) return;
	el.style.display = "";
	el.classList.remove("slided-in");
}

function _slideIn(el: HTMLElement | null) {
	if (!el?.hasChildNodes()) return;

	el.style.display = "block";
	requestAnimationFrame(() => el.classList.add("slided-in"));
}

type TextContent = {
	text: string;
	underline?: boolean;
};

type VoucherContent = {
	text: string;
	voucher: boolean;
	cta?: string;
};

type BanderoleContent = string | TextContent | VoucherContent;

type ModalContent = {
	headline?: string;
	text: string;
};

type BanderoleSettings = {
	content: BanderoleContent[];
	modal?: ModalContent;
	url?: string;
	color?:
		| "black"
		| "red"
		| "ebony"
		| "smokyQuartz"
		| "actionColor"
		| "champagne"
		| "gold"
		| "pearl"
		| "silver"
		| "easterOrange"
		| "easterPink"
		| "birthday";
	position?: "top" | "bottom";
};

function show({ content, modal, url, color, position }: BanderoleSettings) {
	if (!banderoleEl) return;
	const modalId = modal?.text ? "js-banderole-modal" : null;
	banderoleEl.innerHTML = _setBanderoleContent(content, modalId, url);
	_attachEventListeners(banderoleEl);

	if (modal && !modal.headline) {
		const hasVoucher = content.some((item) => typeof item !== "string" && "voucher" in item);
		modal.headline = hasVoucher ? "Gutscheinbedingungen" : "Aktionsbedingungen";
	}
	_setModalContent(modalId, modal);

	banderoleEl.classList.add(..._getTheme(color));
	_setBanderolePosition(position);
	_isCartPage() ? _hide(banderoleEl) : _slideIn(banderoleEl);
}

function showCart({ content, modal, url }: Pick<BanderoleSettings, "content" | "modal" | "url">) {
	if (!cartBanderoleEl) return;
	const modalId = modal?.text ? "js-cart-banderole-modal" : null;
	cartBanderoleEl.innerHTML = _setBanderoleContent(content, modalId, url);
	_attachEventListeners(cartBanderoleEl);
	_setModalContent(modalId, modal);
	_slideIn(cartBanderoleEl);
}

function _setBanderoleContent(content: BanderoleContent[], modalId: string | null, url?: string) {
	const HTMLContent = content
		.map((item) => {
			if (typeof item === "string") return item;

			// voucher code
			if ("voucher" in item) {
				if (item.voucher && isAuthenticated) {
					return `
						<div class="c-banderole-voucher">
							<button
								type="button"
								class="js-banderole-copy-button c-banderole-copy-button font-semibold"
								data-voucher-code="${item.text}"
							>
								<svg class="svg-icon size-4" aria-hidden="true"><use href="#icon-copy"></use></svg>
								${item.text}
							</button>
							${item.cta ? `<span class="c-banderole-cta underline">${item.cta}</span>` : ""}
						</div>`;
				}
				return item.text;
			}

			if (item.underline) {
				return `<span class="underline">${item.text}</span>`;
			}

			return item.text;
		})
		.join(" ");

	const accessibleLink = url ? `<a href="${url}" class="js-banderole-link sr-only">zur Aktion</a>` : "";

	if (!modalId) return `<div class="o-page-wrap c-banderole-content py-3">${HTMLContent}</div> ${accessibleLink}`;

	return `
		<div class="o-page-wrap c-banderole-content c-banderole-grid py-3">
			<div>${HTMLContent}</div>
			<button
				type="button"
				class="js-banderole-modal-button c-banderole-modal-button"
				data-modal-id="${modalId}"
				aria-label="Hinweis anzeigen"
			>
				<svg class="svg-icon size-4" aria-hidden="true"><use href="#icon-info"></use></svg>
			</button>
		</div>
		${accessibleLink}`;
}

function _setModalContent(modalId: string | null, modalContent?: ModalContent) {
	if (!modalId || !modalContent?.text) return;

	const modalEl = document.getElementById(modalId);
	if (!modalEl) return;
	const modalHeaderEl = modalEl.querySelector<HTMLElement>(".info-modal__header");
	if (modalHeaderEl) modalHeaderEl.innerText = modalContent.headline ?? "Hinweis";
	const modalBodyEl = modalEl.querySelector<HTMLElement>(".info-modal__body");
	if (modalBodyEl) modalBodyEl.innerText = modalContent.text;
}

function _attachEventListeners(el: HTMLElement | null) {
	if (!el) return;
	el.addEventListener("click", _onBanderoleClick);
	el.querySelector<HTMLButtonElement>(".js-banderole-modal-button")?.addEventListener("click", (e) => {
		e.stopPropagation();
		const triggerEl = e.currentTarget as HTMLButtonElement;
		_openModal(triggerEl);
	});
	el.querySelector<HTMLButtonElement>(".js-banderole-copy-button")?.addEventListener("click", (e) => {
		e.stopPropagation();
		const triggerEl = e.currentTarget as HTMLButtonElement;
		_copyToClipboard(triggerEl.dataset.voucherCode);
	});
}

function _onBanderoleClick({ currentTarget }: Event) {
	const linkEl = (currentTarget as HTMLElement).querySelector<HTMLAnchorElement>(".js-banderole-link");
	if (!linkEl) return;

	const voucherCode = (currentTarget as HTMLElement).querySelector<HTMLButtonElement>(".js-banderole-copy-button")
		?.dataset.voucherCode;
	pushGA4CustomEvent("event_generic", {
		event_action: "click",
		event_category: "banderole-link",
		event_label: voucherCode ?? "",
	});
	window.location.href = linkEl.href;
}

const _openModal = (triggerEl: HTMLButtonElement) => {
	const modalId = triggerEl.dataset.modalId;
	if (!modalId) return;
	const modal = document.getElementById(modalId);
	if (!modal) return;

	showModal(modal, triggerEl);
};

async function _copyToClipboard(voucherCode?: string) {
	if (!voucherCode) return;

	try {
		await navigator.clipboard.writeText(voucherCode);
		OcmMessenger.publish(messengerPublicationTypes.ADD_TOAST, { message: "Gutscheincode wurde kopiert" });
		pushGA4CustomEvent("event_generic", {
			event_action: "copy",
			event_category: "banderole",
			event_label: voucherCode,
		});
	} catch (error) {
		console.error("Writing to clipboard failed", error);
	}
}

// eslint-disable-next-line complexity
function _getTheme(color: BanderoleSettings["color"]) {
	const classList: string[] = [];
	switch (color) {
		case "black":
		case "red":
			classList.push(`bg-${color}-primary`, "text-white");
			break;
		case "ebony":
		case "smokyQuartz":
		case "birthday":
			classList.push(`bg-${color}`, "text-white");
			break;
		case "actionColor":
		case "champagne":
		case "gold":
		case "pearl":
		case "silver":
		case "easterOrange":
		case "easterPink":
			classList.push(`bg-${color}`);
			break;
		default:
			classList.push("bg-gray-20", "border-b-gray-40");
			break;
	}

	return classList;
}

function _setBanderolePosition(position: BanderoleSettings["position"]) {
	if (!banderoleEl || position !== "top") return;

	const headerEl = document.getElementById("js-header");
	if (!headerEl) return;
	headerEl.before(banderoleEl);
	banderoleEl.classList.add("is-top");
}

export { show, showCart };
